import { useState } from 'react';
import { workflow } from '../api/endpoints';
import type { Notification } from '../api/types';
import { Card, ErrorBox, fmtDate, label, PageHeader, StatusBadge } from '../components/ui';
import { useAsync } from '../hooks';

/** Ссылка перехода из уведомления: заявка, входящее сообщение или произвольный URL. */
function notifLink(n: Notification): { href: string; text: string } | null {
  const p = (n.payload ?? {}) as Record<string, unknown>;
  const req = p.request_id ?? p.request;
  if (typeof req === 'string' && req) return { href: `#/requests/${req}`, text: 'Открыть заявку' };
  const url = p.url ?? p.link;
  if (typeof url === 'string' && url) return { href: url.startsWith('/') ? `#${url}` : url, text: 'Открыть' };
  if (p.message_id || p.inbox || n.type === 'inbound_message' || n.type === 'inbox') {
    return { href: '#/inbox', text: 'Перейти во входящие' };
  }
  return null;
}

export function NotificationsPage() {
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [actionErr, setActionErr] = useState<unknown>(null);

  const list = useAsync<Notification[]>(
    () => workflow.notifications(onlyUnread ? { read_at__isnull: true } : undefined),
    [onlyUnread],
  );

  const markRead = async (id: string) => {
    setActionErr(null);
    try {
      await workflow.markRead(id);
      list.reload();
    } catch (x) { setActionErr(x); }
  };

  const markAll = async () => {
    const unread = (list.data ?? []).filter((n) => !n.read_at);
    setActionErr(null);
    for (const n of unread) {
      try { await workflow.markRead(n.id); } catch (x) { setActionErr(x); }
    }
    list.reload();
  };

  const rows = list.data ?? [];
  const unreadCount = rows.filter((n) => !n.read_at).length;

  return (
    <div>
      <PageHeader title={`Уведомления${unreadCount ? ` (непрочитанных: ${unreadCount})` : ''}`}>
        <button className="btn" onClick={markAll} disabled={unreadCount === 0}>Отметить все прочитанными</button>
      </PageHeader>
      <div className="toolbar">
        <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} />
          Только непрочитанные
        </label>
        <button className="btn" onClick={list.reload}>Обновить</button>
      </div>
      <Card>
        <ErrorBox error={actionErr} />
        <ErrorBox error={list.error} onRetry={list.reload} />
        {rows.length === 0 && !list.loading && <div className="empty">Уведомлений нет</div>}
        {rows.map((n) => (
          <div key={n.id} className={`notif-item ${n.read_at ? '' : 'unread'}`}>
            <div className="notif-body">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <StatusBadge value={n.type} />
                <strong>{n.title ?? label(n.type) ?? 'Уведомление'}</strong>
                <span className="muted">{fmtDate(n.created_at)}</span>
              </div>
              {n.body && <div style={{ marginTop: 4 }}>{n.body}</div>}
              {!n.body && n.payload && Object.keys(n.payload).length > 0 && (
                <div className="muted" style={{ marginTop: 4 }}>{JSON.stringify(n.payload)}</div>
              )}
              {notifLink(n) && (
                <div style={{ marginTop: 4 }}>
                  <a href={notifLink(n)!.href}>{notifLink(n)!.text} →</a>
                </div>
              )}
            </div>
            <div>
              {n.read_at
                ? <span className="muted">Прочитано {fmtDate(n.read_at)}</span>
                : <button className="btn small" onClick={() => void markRead(n.id)}>Прочитано</button>}
            </div>
          </div>
        ))}
        {list.loading && <div className="loading">Загрузка уведомлений…</div>}
      </Card>
    </div>
  );
}
